import React, { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import {
  useAnalysisTypes,
  useEfficiencies,
  useEfficiencyParts,
  useEquipmentMide,
  useEquipment,
  useUser,
} from "@services/servicesGenericsTables";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { useNavigate, useParams } from "react-router-dom";
import {
  EfficiencyService,
  IFormNewEfficiency,
} from "@app/types/calibration.types";
import dayjs from "dayjs";
import {
  efficienciesIGMapping,
  efficienciesMapping,
  mapEfficiencyParts,
} from "../../utils/helpers";
import GammaForm from "./gammaForm";
import AlfaBetaForm from "./alfaBetaForm";
import CentelleoForm from "./centelleoForm";
import { EFFICIENCY_TYPE } from "../../utils/constants";
import { UserType } from "@app/types/genericTables.types";
import useAuth from "@app/context/AuthContext";
import MessageDialog from "@app/components/Login/MessageDialog";
import { errorAlert } from "@app/utils/alerts";
import { toExponentialUppercase } from "@app/utils/functions";
import { KeysTabsAnalysis } from "@app/types/samples.types";

const FORM_IG_EFFICIENCY = {
  type: EFFICIENCY_TYPE.Gamma,
  form: GammaForm,
  mapping: efficienciesIGMapping,
  function: useEfficiencies.getState().save,
  key: Object.keys(EFFICIENCY_TYPE)[0],
};

const FORM_EFFICIENCY_TYPE = [
  {
    type: EFFICIENCY_TYPE.Gamma,
    form: GammaForm,
    mapping: efficienciesMapping,
    function: useEfficiencies.getState().save,
    key: Object.keys(EFFICIENCY_TYPE)[0],
  },
  {
    type: EFFICIENCY_TYPE.AlfaBeta,
    form: AlfaBetaForm,
    mapping: mapEfficiencyParts,
    function: useEfficiencies.getState().save,
    key: Object.keys(EFFICIENCY_TYPE)[1],
  },
  {
    type: EFFICIENCY_TYPE.LiquidScintillation,
    form: CentelleoForm,
    mapping: efficienciesMapping,
    function: useEfficiencies.getState().save,
    key: Object.keys(EFFICIENCY_TYPE)[2],
  },
];

const getDefaultValues = (efficiency?: EfficiencyService, user?: UserType) => {
  const today = dayjs();

  const efficiencyType = FORM_EFFICIENCY_TYPE.find(
    (type) =>
      type.key.toLowerCase() === efficiency?.efficiency_type?.toLowerCase()
  );
  
  // Convertir el campo part (string) a un array de objetos para el formulario
  const partString = efficiency?.part || "";
  const partsForForm = partString 
    ? partString.split(", ").map((partName, index) => ({
        id: `part-${index}`,
        parte: partName,
        part_name: partName,
      }))
    : [];
  
  const partsObject = partsForForm.reduce((acc: any, part: any) => {
    acc[part.id] = {  // Usar part.id ("part-0", "part-1"...) en lugar de index
      efficiency: efficiency?.efficiency ?? undefined,
      a: efficiency?.a ?? undefined,
      x: efficiency?.x ?? undefined,
      x2: efficiency?.x2 ?? undefined,
      x3: efficiency?.x3 ?? undefined,
      x4: efficiency?.x4 ?? undefined,
    };
    return acc;
  }, {});

  return {
    id: efficiency?.id || undefined,
    date: efficiency?.date ? efficiency.date : today,
    equipment: efficiency?.equipment || undefined,
    cs_source_geometry: efficiency?.cs_source_geometry || undefined,
    efficiency: efficiency?.efficiency ?? undefined,
    a: efficiency?.a ?? undefined,
    x: efficiency?.x ?? undefined,
    x2: efficiency?.x2 ?? undefined,
    x3: efficiency?.x3 ?? undefined,
    x4: efficiency?.x4 ?? undefined,
    internal_comments: efficiency?.internal_comments || "",
    efficiency_type: efficiencyType || null,
    analyst: efficiency?.analyst || user,
    part: partsForForm, // Array mapeado con parte y part_name
    parts: partsObject,
    analysis: efficiency?.analysis || [],
    cs_source_activity_bq: efficiency?.cs_source_activity_bq ?? undefined,
    date_activity: efficiency?.date_activity ?? today,
    cs_gpd: efficiency?.cs_gpd
      ? toExponentialUppercase(parseFloat(efficiency?.cs_gpd), 3)
      : undefined,
    cs_decay_constant: efficiency?.cs_decay_constant
      ? toExponentialUppercase(parseFloat(efficiency?.cs_decay_constant), 3)
      : undefined,
    date_cs_source_counting: efficiency?.date_cs_source_counting ?? today,
    detector: efficiency?.detector ? efficiency.detector : undefined,
    cs_source_counting_time: efficiency?.cs_source_counting_time ?? undefined,
    cs_time_increment_from_counting_date:
      efficiency?.cs_time_increment_from_counting_date ?? undefined,
    cs_source_gps: efficiency?.cs_source_gps ?? undefined,
    cs_source_pattern_count: efficiency?.cs_source_pattern_count ?? undefined,
    cs_fondo_count_1: efficiency?.cs_fondo_count_1 ?? undefined,
    cs_fondo_counting_time_1: efficiency?.cs_fondo_counting_time_1 ?? undefined,
    cs_fondo_count_2: efficiency?.cs_fondo_count_2 ?? undefined,
    cs_fondo_counting_time_2: efficiency?.cs_fondo_counting_time_2 ?? undefined,
    avg_integral_fondo: efficiency?.avg_integral_fondo ?? undefined,
    cs_source_pattern_net_area:
      efficiency?.cs_source_pattern_net_area ?? undefined,
    cs_source_pattern_efficiency:
      efficiency?.cs_source_pattern_efficiency ?? undefined,
  };
};

interface CalibrationEfficiencyFormProps {
  mode: string;
}

const CalibrationEfficiencyForm = ({
  mode,
}: CalibrationEfficiencyFormProps) => {
  const { userList } = useUser();
  const { idCalibration } = useParams();
  const navigate = useNavigate();
  const { save, get, fetchAll } = useEfficiencies();
  const { analysisTypeList } = useAnalysisTypes();
  const { fetchByAnalysisType, equipmentMideListFiltered, loading } = useEquipmentMide();
  const { equipmentList } = useEquipment();
  const { efficiencyPartsList } = useEfficiencyParts();
  const { user } = useAuth();
  const isView = mode === MODES_OPTIONS.VIEW;
  const isEdit = mode === MODES_OPTIONS.EDIT;

  const [efficiencyPreload, setEfficiencyPreload] = useState<
    EfficiencyService | undefined
  >();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [dataToSave, setDataToSave] = useState<IFormNewEfficiency | null>(null);

  const handleSave = (data: IFormNewEfficiency) => {
    if (isEdit) {
      const dataSaved = { ...data, id: Number(idCalibration) };
      setDataToSave(dataSaved);
      setOpenConfirmModal(true);
    } else submit(data);
  };

  const handleDialogClose = () => setOpenConfirmModal(false);

  const handleDialogConfirm = () => {
    if (dataToSave) {
      submit(dataToSave);
    }
    handleDialogClose();
  };

  useEffect(() => {
    if (idCalibration) {
      const calibration = get(Number(idCalibration));
      if (!calibration) {
        errorAlert("No se encontró la calibración");
        navigate("/admin/calibraciones");
        return;
      }
      setEfficiencyPreload(calibration);
    }
  }, [idCalibration]);

  const form = useForm<any>({
    mode: "all",
    defaultValues: getDefaultValues(efficiencyPreload, user),
  });

  const watchEfficiency = form.watch("efficiency_type");
  const watchAnalysis = form.watch("analysis");

  useEffect(() => {
    const fetchData = async () => {
      // Si analysis es un array, usar el primer elemento para fetchByAnalysisType
      const firstAnalysis = Array.isArray(watchAnalysis) ? watchAnalysis[0] : watchAnalysis;
      if (firstAnalysis?.id) {
        await fetchByAnalysisType(firstAnalysis.id);
      }
    };
    if (watchAnalysis && (Array.isArray(watchAnalysis) ? watchAnalysis.length > 0 : watchAnalysis)) {
      fetchData();
    }
  }, [watchAnalysis, watchEfficiency]);

  useEffect(() => {
    if (!idCalibration) {
      if (watchEfficiency?.key === Object.keys(EFFICIENCY_TYPE)[2]) {
        const equipment = equipmentMideListFiltered.find(
          (equipment) => equipment.codigo === "3002" //CODIGO DE EQUIPO DE CENTELLEO
        );
        //eslint-disable-next-line
        form.setValue("equipment", (equipment as any) || undefined);
      }
    }
  }, [equipmentMideListFiltered]);

  useEffect(() => {
    if (efficiencyPreload) {
      form.reset(getDefaultValues(efficiencyPreload, user));
    }
  }, [efficiencyPreload, form, user]);

  const handleBack = async () => {
    await fetchAll(); // Esperar a que termine la actualización de datos
    navigate("/admin/calibraciones", { 
      replace: true,
      state: { timestamp: Date.now() } // Forzar actualización
    });
  };

  const selectedFormComponent = FORM_EFFICIENCY_TYPE.find(
    (efficiencyType) => efficiencyType.type === watchEfficiency?.type
  )?.form;

  const submit = async (data: IFormNewEfficiency) => {
   
    let selectedMapping;
    
    // Obtener el primer análisis si es array, o el análisis único
    const firstAnalysis = Array.isArray(watchAnalysis) ? watchAnalysis[0] : watchAnalysis;

    if (
      watchEfficiency?.key === EFFICIENCY_TYPE.Gamma &&
      firstAnalysis?.codigo === KeysTabsAnalysis.IG
    ) {
      selectedMapping = FORM_IG_EFFICIENCY;
    } else {
      selectedMapping = FORM_EFFICIENCY_TYPE.find(
        (efficiencyType) => efficiencyType.type === watchEfficiency?.type
      );
    }
    const mapping = selectedMapping?.mapping;
    const selectedFunction = selectedMapping ? selectedMapping.function : save;

    if (selectedFunction && mapping) {
      const mappedData = mapping(data);
      
      // Si es un array con múltiples elementos (creación de múltiples eficiencias), usar saveMultiple
      if (Array.isArray(mappedData) && mappedData.length > 1) {
        const { saveMultiple } = useEfficiencies.getState();
        const resp = await saveMultiple(mappedData);
        if (!resp) {
          await handleBack();
        }
      } else {
        // Si es una sola eficiencia (edición o creación con una parte), usar save
        const dataToSave = Array.isArray(mappedData) ? mappedData[0] : mappedData;
        
        const resp = await selectedFunction(
          dataToSave,
          idCalibration ? Number(idCalibration) : undefined,
          firstAnalysis?.codigo
        );
        if (!resp) {
          await handleBack();
        }
      }
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <CreatePageList
          title={
            isView
              ? `Ver calibración de eficiencia - CE${idCalibration}`
              : idCalibration
              ? `Editar calibración de eficiencia - CE${idCalibration}`
              : `Nueva calibración de eficiencia`
          }
          footer={
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              {!isView && (
                <MButton
                  variant="contained"
                  color="primary"
                  text="Guardar calibración"
                  type="submit"
                />
              )}
              <MButton
                variant="outlined"
                color="secondary"
                sx={{ ml: 2 }}
                text="Cancelar"
                onClick={handleBack}
              />
            </Box>
          }
        >
          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              name="analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
              disabled={isView}
            />
            <MAutocomplete
              key={JSON.stringify(FORM_EFFICIENCY_TYPE)}
              name="efficiency_type"
              options={FORM_EFFICIENCY_TYPE}
              label="Seleccione una eficiencia"
              getOptionLabel={(option) => option.type || ""}
              validation={{ required: "La eficiencia es requerida" }}
              disabled={isView || isEdit}
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Nueva eficiencia "}
              {watchEfficiency?.type}
            </Typography>
          </Grid>

          <ResponsiveFormContainer columns={4}>
            <MDatePicker
              name="date"
              pickerType="date"
              label="Fecha eficiencia"
              validation={{ required: "La fecha es requerida" }}
              disabled={isView}
            />
            <MAutocomplete
              key={`${watchEfficiency?.type}`}
              name="analysis"
              options={analysisTypeList}
              label="Análisis"
              getOptionLabel={(option) => option.descripcion}
              disabled={isView}
              multiple
            />
            <MAutocomplete
              key={JSON.stringify(form.watch("equipment"))}
              name="equipment"
              options={equipmentMideListFiltered}
              label="Seleccione un Equipo"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El equipo es requerido" }}
              disabled={isView || !watchEfficiency}
              loading={watchEfficiency && loading}
              helperText={!watchAnalysis || (Array.isArray(watchAnalysis) && watchAnalysis.length === 0) ? "Seleccione un análisis" : ""}
            />
            {watchEfficiency?.type ===
                EFFICIENCY_TYPE.Gamma && 
                (Array.isArray(watchAnalysis) ? watchAnalysis[0]?.codigo : watchAnalysis?.codigo) === KeysTabsAnalysis.IG && (
              <MAutocomplete
                key={JSON.stringify(form.watch("cs_source_geometry"))}
                name="cs_source_geometry"
                options={equipmentList.filter(
                  (e) => e.is_geometry === true
                )}
                label="Seleccione una geometría"
                getOptionLabel={(option) =>
                  `${option.codigo} - ${option.descripcion}` || ""
                }
                validation={{ required: "La geometría es requerida" }}
                disabled={isView || !watchEfficiency}
                loading={watchEfficiency && loading}
                helperText={!watchAnalysis || (Array.isArray(watchAnalysis) && watchAnalysis.length === 0) ? "Seleccione un análisis" : ""}
              />
            )}
            {watchEfficiency?.type ===
              "Contadores proporcionales Alfa-Beta" && (
              <MAutocomplete
                key={JSON.stringify(watchAnalysis)}
                name="part"
                options={
                  watchAnalysis && (Array.isArray(watchAnalysis) ? watchAnalysis.length > 0 : watchAnalysis)
                    ? efficiencyPartsList.filter(
                        (part) => {
                          const analysisCode = Array.isArray(watchAnalysis) ? watchAnalysis[0]?.codigo : watchAnalysis?.codigo;
                          // Filtrar partes: excluir "SO" cuando el análisis es BPP (Beta partículas de polvo)
                          if (analysisCode === KeysTabsAnalysis.BPP && part.parte === 'SO') {
                            return false;
                          }
                          return part.analisis === analysisCode;
                        }
                      )
                    : []
                }
                label="Parte"
                getOptionLabel={(option) => option.parte || ""}
                helperText={!watchAnalysis || (Array.isArray(watchAnalysis) && watchAnalysis.length === 0) ? "Seleccione un análisis" : ""}
                disabled={!watchAnalysis || (Array.isArray(watchAnalysis) && watchAnalysis.length === 0) || isView}
                multiple
                validation={{
                  required:
                    efficiencyPartsList.filter(
                      (part) => {
                        const analysisCode = Array.isArray(watchAnalysis) ? watchAnalysis[0]?.codigo : watchAnalysis?.codigo;
                        // Aplicar el mismo filtro para la validación
                        if (analysisCode === KeysTabsAnalysis.BPP && part.parte === 'SO') {
                          return false;
                        }
                        return part.analisis === analysisCode;
                      }
                    )?.length > 0
                      ? "La parte es requerida"
                      : false,
                }}
              />
            )}
          </ResponsiveFormContainer>

          {selectedFormComponent &&
            React.createElement(selectedFormComponent, { mode })}

          <ResponsiveFormContainer columns={3} mt={2}>
            <MTextField
              name="internal_comments"
              label="Comentarios Internos"
              placeholder="Comentarios Internos"
              type="textArea"
              rows={4}
              multiline
              fullWidth
              disabled={isView}
            />
          </ResponsiveFormContainer>
        </CreatePageList>
      </form>

      <MessageDialog
        data={{
          component: "Confirm",
          title:
            "Va a proceder a Editar esta calibración y puede haber cálculos realizados sobre éstos datos",
          message: "¿Desea continuar?",
          status: "confirm",
          active: openConfirmModal,
          handleConfirm: handleDialogConfirm,
        }}
        handleClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
      />
    </FormProvider>
  );
};

export default CalibrationEfficiencyForm;
